// pages/CreateInvoicePage.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import apiClient from '@/lib/api-client';
import { ArrowLeft, Save, FileText, Truck, Store, Calendar } from 'lucide-react';

export function CreateInvoicePage() {
 const navigate = useNavigate();
 const user = useAuthStore((s) => s.user);
 const queryClient = useQueryClient();
 const addToast = useToastStore((s) => s.addToast);

 const [form, setForm] = useState({
 vendorId: '', poId: '', grnId: '',
 invoiceNumber: '', referenceNumber: '',
 subtotal: '', taxAmount: '', totalAmount: '',
 invoiceDate: new Date().toISOString().slice(0, 10),
 dueDate: '', paymentTerms: '', notes: '',
 });

 const { data: vendors } = useQuery({
 queryKey: ['vendors-inv', user?.tenantId],
 queryFn: async () => {
 const { data } = await apiClient.get(`/tenants/${user!.tenantId}/vendors`, { params: { limit: 50 } });
 if (Array.isArray(data)) return data;
 if (data?.data && Array.isArray(data.data)) return data.data;
 return [];
 },
 enabled: !!user?.tenantId,
 });

 const { data: purchaseOrders } = useQuery({
 queryKey: ['pos-inv', user?.tenantId],
 queryFn: async () => {
 const { data } = await apiClient.get(`/tenants/${user!.tenantId}/purchase-orders`, { params: { limit: 50, status: 'sent' } });
 if (Array.isArray(data)) return data;
 if (data?.data && Array.isArray(data.data)) return data.data;
 return [];
 },
 enabled: !!user?.tenantId,
 });

 const createInvoiceMutation = useMutation({
 mutationFn: async () => {
 if (!form.vendorId) throw new Error('Select a vendor.');
 if (!form.invoiceNumber) throw new Error('Enter invoice number.');
 if (!form.totalAmount || parseFloat(form.totalAmount) <= 0) throw new Error('Enter a valid total amount.');

 const { data } = await apiClient.post(`/tenants/${user!.tenantId}/invoices`, {
 ...form,
 poId: form.poId || undefined,
 grnId: form.grnId || undefined,
 dueDate: form.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
 balanceDue: form.totalAmount,
 });
 return data;
 },
 onSuccess: () => {
 addToast({ type: 'success', title: 'Invoice recorded', message: 'Vendor invoice has been recorded.' });
 queryClient.invalidateQueries({ queryKey: ['invoices'] });
 navigate('/invoices');
 },
 onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.response?.data?.error?.message || err?.message }),
 });

 const vendorsList = (vendors || []) as any[];
 const posList = (purchaseOrders || []) as any[];

 // Auto-calculate due date from vendor payment terms
 const selectedVendor = vendorsList.find((v: any) => v.id === form.vendorId);
 const handleVendorChange = (vendorId: string) => {
 const vendor = vendorsList.find((v: any) => v.id === vendorId);
 setForm({ ...form, vendorId, paymentTerms: vendor?.paymentTerms || '' });
 if (vendor?.paymentTerms) {
 const days = parseInt(vendor.paymentTerms.replace(/[^0-9]/g, '')) || 30;
 const dueDate = new Date();
 dueDate.setDate(dueDate.getDate() + days);
 setForm(prev => ({ ...prev, dueDate: dueDate.toISOString().slice(0, 10), vendorId }));
 }
 };

 // Auto-fill from PO
 const handlePOChange = (poId: string) => {
 const po = posList.find((p: any) => p.id === poId);
 if (po) {
 setForm({
 ...form, poId,
 vendorId: po.vendorId || form.vendorId,
 subtotal: po.subtotal || form.subtotal,
 taxAmount: po.taxAmount || form.taxAmount,
 totalAmount: po.totalAmount || form.totalAmount,
 });
 } else {
 setForm({ ...form, poId });
 }
 };

 const formatNaira = (val: string) => `₦${parseFloat(val || '0').toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

 return (
 <div className="space-y-5 animate-fade-in max-w-3xl mx-auto">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <Button variant="ghost" size="sm" onClick={() => navigate('/invoices')}><ArrowLeft size={16} className="mr-1" /> Back</Button>
 <div className="p-2 rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 shadow-lg shadow-teal-500/20"><FileText size={18} className="text-white" /></div>
 <div><h1 className="text-xl font-bold text-slate-900 ">Record Vendor Invoice</h1><p className="text-xs text-slate-500">Record a bill from a vendor</p></div>
 </div>
 </div>

 <Card>
 <CardContent className="p-5 space-y-4">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5"><Truck size={12} className="inline mr-1" /> Vendor *</label>
 <select value={form.vendorId} onChange={(e) => handleVendorChange(e.target.value)}
 className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white " required>
 <option value="">Select vendor...</option>
 {vendorsList.map((v: any) => <option key={v.id} value={v.id}>{v.name}</option>)}
 </select>
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Purchase Order (optional)</label>
 <select value={form.poId} onChange={(e) => handlePOChange(e.target.value)}
 className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white ">
 <option value="">No PO</option>
 {posList.map((po: any) => <option key={po.id} value={po.id}>{po.poNumber} — {formatNaira(po.totalAmount)}</option>)}
 </select>
 </div>
 </div>
 <div className="grid grid-cols-2 gap-4">
 <Input label="Invoice Number *" value={form.invoiceNumber} onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })} placeholder="INV-2026-001" required />
 <Input label="Vendor Reference" value={form.referenceNumber} onChange={(e) => setForm({ ...form, referenceNumber: e.target.value })} />
 </div>
 <div className="grid grid-cols-3 gap-4">
 <Input label="Subtotal (₦)" type="number" step="0.01" value={form.subtotal} onChange={(e) => setForm({ ...form, subtotal: e.target.value })} />
 <Input label="Tax Amount (₦)" type="number" step="0.01" value={form.taxAmount} onChange={(e) => setForm({ ...form, taxAmount: e.target.value })} />
 <Input label="Total Amount (₦) *" type="number" step="0.01" value={form.totalAmount} onChange={(e) => setForm({ ...form, totalAmount: e.target.value })} required />
 </div>
 <div className="grid grid-cols-3 gap-4">
 <Input label="Invoice Date *" type="date" value={form.invoiceDate} onChange={(e) => setForm({ ...form, invoiceDate: e.target.value })} required />
 <Input label="Due Date *" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} required />
 <Input label="Payment Terms" value={form.paymentTerms} onChange={(e) => setForm({ ...form, paymentTerms: e.target.value })} placeholder="Net 30" />
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Notes</label>
 <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
 className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white resize-none" />
 </div>
 </CardContent>
 </Card>

 <div className="flex justify-end gap-3">
 <Button variant="secondary" onClick={() => navigate('/invoices')}>Cancel</Button>
 <Button onClick={() => createInvoiceMutation.mutate()} isLoading={createInvoiceMutation.isPending} size="lg">
 <Save size={16} className="mr-1.5" /> Record Invoice
 </Button>
 </div>
 </div>
 );
}